import { useRef, useEffect } from "react";

interface TimeSelectorProps {
  value: {
    hour: number;
    minute: number;
    period: "am" | "pm";
  };
  onChange: (value: { hour: number; minute: number; period: "am" | "pm" }) => void;
  selectedDate?: string;
  currentDate?: string;
}

export function TimeSelector({ value, onChange, selectedDate, currentDate }: TimeSelectorProps) {
  const hourRef = useRef<HTMLDivElement>(null);
  const minuteRef = useRef<HTMLDivElement>(null);
  const periodRef = useRef<HTMLDivElement>(null);

  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  const minutes = [0, 15, 30, 45];
  const periods: ("am" | "pm")[] = ["am", "pm"];

  const scrollTimers = useRef<{ [key: string]: NodeJS.Timeout }>({});

  const parseDate = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(Date.UTC(year, month - 1, day));
  };

  const isNextDayBooking = () => {
    if (!selectedDate || !currentDate) return false;
    const selected = parseDate(selectedDate);
    const current = parseDate(currentDate);
    const diffDays = Math.round((selected.getTime() - current.getTime()) / (1000 * 60 * 60 * 24));
    return diffDays === 1;
  };

  // Check if a date falls on the weekend
  const isWeekendDate = (dateStr: string) => {
    if (!dateStr) return false;
    const date = new Date(dateStr + 'T12:00:00');
    const dayOfWeek = date.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6; // 0 = Sunday, 6 = Saturday
  };

  // Determine if a specific time slot should be considered unavailable
  const isTimeInPast = (hour: number, minute: number, period: "am" | "pm") => {
    // Disable all times on weekends
    if (selectedDate && isWeekendDate(selectedDate)) {
      return true;
    }
    
    if (!selectedDate || !currentDate) {
      return false;
    }

    const nextDayBooking = isNextDayBooking();

    if (nextDayBooking) {
      let selectedHour24 = hour;
  if (period === "pm" && hour !== 12) selectedHour24 = hour + 12;
  if (period === "am" && hour === 12) selectedHour24 = 0;

      if (selectedHour24 < 12) {
        return true;
      }
}

if (selectedDate !== currentDate) {
  let selectedHour24 = hour;
  if (period === "pm" && hour !== 12) selectedHour24 = hour + 12;
  if (period === "am" && hour === 12) selectedHour24 = 0;
  return selectedHour24 < 12;
}

  const now = new Date();
  const torontoTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/Toronto' }));
  const currentHour24 = torontoTime.getHours();
  const currentMinute = torontoTime.getMinutes();

  let selectedHour24 = hour;
  if (period === "pm" && hour !== 12) selectedHour24 = hour + 12;
  if (period === "am" && hour === 12) selectedHour24 = 0;

  const totalCurrentMinutes = currentHour24 * 60 + currentMinute;
  const totalSelectedMinutes = selectedHour24 * 60 + minute;

  return totalSelectedMinutes <= totalCurrentMinutes;
  };

  // Determine if an AM/PM period contains at least one valid time slot
  const hasValidTimeInPeriod = (period: "am" | "pm") => {
    // Weekends block all time options
    if (selectedDate && isWeekendDate(selectedDate)) {
      return false;
    }
    
    if (!selectedDate || !currentDate) {
      return true;
    }

    const nextDayBooking = isNextDayBooking();

    if (nextDayBooking) {
      if (period === "am") {
        return false;
      }
      return true;
    }

    if (selectedDate !== currentDate) {
      return period !== "am";
    }
    
    const now = new Date();
    const torontoTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/Toronto' }));
    const currentHour24 = torontoTime.getHours();
    const currentMinute = torontoTime.getMinutes();
    
    // Buffer de 1 hora
    const bufferHours = 1;
    const bufferMinutes = bufferHours * 60;
    const totalCurrentMinutes = currentHour24 * 60 + currentMinute + bufferMinutes;
    
    // Verificar todas as combinações possíveis de hora/minuto neste período
    for (const hour of hours) {
      for (const minute of minutes) {
        // Converter para 24h
        let hour24 = hour;
        if (period === "pm" && hour !== 12) hour24 = hour + 12;
        if (period === "am" && hour === 12) hour24 = 0;
        
        const totalMinutes = hour24 * 60 + minute;
        
        // Se encontrarmos pelo menos uma combinação válida, o período está disponível
        if (totalMinutes > totalCurrentMinutes) {
          return true;
        }
      }
    }
    
    return false;
  };

  useEffect(() => {
    // Scroll to the initially selected values
    scrollToSelected();
  }, []);

  const scrollToSelected = () => {
    const itemHeight = 44;
    
    if (hourRef.current) {
      const hourIndex = hours.indexOf(value.hour);
      hourRef.current.scrollTop = hourIndex * itemHeight;
    }
    
    if (minuteRef.current) {
      const minuteIndex = minutes.indexOf(value.minute);
      minuteRef.current.scrollTop = minuteIndex * itemHeight;
    }
    
    if (periodRef.current) {
      const periodIndex = periods.indexOf(value.period);
      periodRef.current.scrollTop = periodIndex * itemHeight;
    }
  };

  const handleScroll = (
    type: "hour" | "minute" | "period",
    scrollTop: number,
    items: any[]
  ) => {
    // Clear any previous debounce timer
    if (scrollTimers.current[type]) {
      clearTimeout(scrollTimers.current[type]);
    }

    // Debounce to snap smoothly after scrolling stops
    scrollTimers.current[type] = setTimeout(() => {
      const itemHeight = 44;
      const index = Math.round(scrollTop / itemHeight);
      const clampedIndex = Math.max(0, Math.min(index, items.length - 1));
      
      // Snap to the nearest item
      const ref = type === "hour" ? hourRef : type === "minute" ? minuteRef : periodRef;
      if (ref.current) {
        ref.current.scrollTo({
          top: clampedIndex * itemHeight,
          behavior: "smooth"
        });
      }

      // Verify the proposed time before updating state
      let testHour = value.hour;
      let testMinute = value.minute;
      let testPeriod = value.period;
      
      if (type === "hour") {
        testHour = items[clampedIndex];
      } else if (type === "minute") {
        testMinute = items[clampedIndex];
      } else {
        testPeriod = items[clampedIndex];
      }
      
      // For periods, ensure at least one valid combination exists
      // For hours/minutes, ensure the specific time isn't invalid
      if (type === "period") {
        if (!hasValidTimeInPeriod(testPeriod)) {
          return;
        }
      } else {
        // Don't update when the combination is invalid
        if (isTimeInPast(testHour, testMinute, testPeriod)) {
          return;
        }
      }

      // Update the external state with the snapped value
      if (type === "hour") {
        onChange({ ...value, hour: items[clampedIndex] });
      } else if (type === "minute") {
        onChange({ ...value, minute: items[clampedIndex] });
      } else {
        onChange({ ...value, period: items[clampedIndex] });
      }
    }, 150);
  };

  const renderColumn = (
    ref: React.RefObject<HTMLDivElement>,
    items: any[],
    selectedValue: any,
    type: "hour" | "minute" | "period",
    suffix: string = ""
  ) => {
    return (
      <div className="relative w-20">
        <div
          ref={ref}
          className="h-[132px] overflow-y-auto scrollbar-hide relative cursor-pointer"
          onScroll={(e) => {
            handleScroll(type, e.currentTarget.scrollTop, items);
          }}
        >
          {/* Padding superior */}
          <div className="h-[44px]" />
          
          {items.map((item, index) => {
            const isSelected = item === selectedValue;
            
            // Determine the time combination based on the current selection and this candidate item
            let testHour = value.hour;
            let testMinute = value.minute;
            let testPeriod = value.period;
            
            if (type === "hour") {
              testHour = item;
            } else if (type === "minute") {
              testMinute = item;
            } else {
              testPeriod = item;
            }
            
            // For periods (AM/PM), verify if at least one valid combination exists
            // For specific hour/minute values, check if the combination is invalid
            const isPast = type === "period" 
              ? !hasValidTimeInPeriod(item)  // Disable period if no valid times remain
              : isTimeInPast(testHour, testMinute, testPeriod);  // Disable the specific time if invalid
            
            return (
              <div
                key={index}
                className={`h-[44px] flex items-center justify-center transition-all duration-200 select-none ${
                  isPast
                    ? "opacity-20 cursor-not-allowed"
                    : isSelected
                    ? "text-foreground text-4xl font-bold opacity-100 cursor-pointer"
                    : "text-muted-foreground text-2xl opacity-40 cursor-pointer"
                }`}
                onClick={() => {
                  if (isPast) return; // Prevent clicks on invalid slots
                  
                  if (type === "hour") {
                    onChange({ ...value, hour: item });
                  } else if (type === "minute") {
                    onChange({ ...value, minute: item });
                  } else {
                    onChange({ ...value, period: item });
                  }
                  
                  // Smoothly scroll to the clicked item
                  if (ref.current) {
                    const itemHeight = 44;
                    ref.current.scrollTo({
                      top: index * itemHeight,
                      behavior: "smooth"
                    });
                  }
                }}
              >
                {type === "minute" ? String(item).padStart(2, "0") : item}
                {isSelected && suffix}
              </div>
            );
          })}
          
          {/* Padding inferior */}
          <div className="h-[44px]" />
        </div>
        
        {/* Linha central indicadora */}
        <div className="absolute top-1/2 left-0 right-0 h-[44px] -translate-y-1/2 pointer-events-none" />
      </div>
    );
  };

  // Check if we're on today's date and whether any times were disabled for being in the past
  const isToday = selectedDate && currentDate && selectedDate === currentDate;
  const isWeekend = selectedDate ? isWeekendDate(selectedDate) : false;
  const hasPastTimes = isToday && !isWeekend; // Only show past-times message for weekdays

  return (
    <div className="w-full">
      <div className="flex items-center justify-center gap-2 py-2 max-w-sm mx-auto">
        {renderColumn(hourRef, hours, value.hour, "hour", "h")}
        <div className="text-3xl font-bold text-foreground">:</div>
        {renderColumn(minuteRef, minutes, value.minute, "minute", "m")}
        {renderColumn(periodRef, periods, value.period, "period")}
      </div>
      {isWeekend && (
        <p className="text-xs text-muted-foreground text-center mt-2">
          Service is not available on weekends
        </p>
      )}
      {hasPastTimes && (
        <p className="text-xs text-muted-foreground text-center mt-2">
          Past times are disabled
        </p>
      )}
    </div>
  );
}

